import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { isAdminOrSupervisor } from '../../lib/roles';
import { ArrowLeft, Loader2, MapPin, Calendar, User, CheckCircle, Flag, Image as ImageIcon, X, Download, Printer } from 'lucide-react';
import { downloadSubmissionPdf, printSubmissionPdf, submissionPdfFilename } from '../../lib/submission-pdf';
import { useAuth } from '../../contexts/AuthContext';
import { format } from 'date-fns';
import { toast } from 'sonner';

function statusPill(status: string) {
  switch (status) {
    case 'submitted': return 'bg-blue-500/20 text-blue-400';
    case 'reviewed': return 'bg-green-500/20 text-green-400';
    case 'flagged': return 'bg-red-500/20 text-red-400';
    case 'draft': return 'bg-yellow-500/20 text-yellow-400';
    default: return 'bg-gray-500/20 text-gray-400';
  }
}

export default function SafetySubmissionDetailScreen() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [reviewStatus, setReviewStatus] = useState<string>('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const isAdmin = isAdminOrSupervisor(user?.role);

  const { data: submissionRaw, isLoading } = useQuery({
    queryKey: ['safety-submission', id],
    queryFn: () => api.safety.getSubmission(id!).then(r => r.data?.data ?? r.data),
    enabled: !!id,
  });
  const submission = submissionRaw as any;

  // Sync review state when data loads
  useEffect(() => {
    if (submission?.review_status && !reviewStatus) setReviewStatus(submission.review_status);
    if (submission?.review_notes && !reviewNotes) setReviewNotes(submission.review_notes);
  }, [submission]);

  const reviewMutation = useMutation({
    mutationFn: () => api.safety.reviewSubmission(id!, { status: reviewStatus, review_notes: reviewNotes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['safety-submission', id] });
      queryClient.invalidateQueries({ queryKey: ['safety-submissions'] });
      toast.success('Review saved');
    },
    onError: () => toast.error('Failed to save review'),
  });

  if (isLoading) {
    return (
      <div className="min-h-full bg-background flex items-center justify-center">
        <Loader2 size={24} className="animate-spin text-fieldiq-accent" />
      </div>
    );
  }

  if (!submission) {
    return (
      <div className="min-h-full bg-background flex flex-col items-center justify-center">
        <p className="text-white text-lg font-semibold mb-2">Submission not found</p>
        <button onClick={() => navigate(-1)} className="text-fieldiq-accent text-sm">Go back</button>
      </div>
    );
  }

  const answers = submission.answers || {};
  // worker_select answers resolved to [{id, name}] by the API; everything else raw.
  const resolved = submission.resolved_answers || {};
  // `visible` is the API's verdict on each field against the stored answers.
  // A field that was hidden when the form was filled was never asked, so it
  // gets no row at all rather than a "—" that reads as a blank answer.
  const fields = (submission.template_fields || submission.fields || []).filter((f: any) => f.visible !== false);
  const submitterName = submission.submitted_by_name || submission.submitter_name || submission.submittedByName;
  const hasGps = Number.isFinite(Number(submission.gps_latitude)) && submission.gps_latitude !== null
    && Number.isFinite(Number(submission.gps_longitude)) && submission.gps_longitude !== null;
  const templateName = submission.template_name || submission.templateName || 'Safety Submission';
  const photos = submission.photos || [];
  const signatures = submission.signatures || [];

  return (
    <div className="min-h-full pb-[var(--bottom-chrome)] bg-background">
      <div className="border-b border-border px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-white hover:text-amber-400 hover:bg-white/10 text-lg font-semibold px-2 py-1 rounded transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
          Back
        </button>
        <div className="flex items-center justify-between mt-2">
          <h1 className="text-base font-bold text-foreground flex items-center gap-2">
            {templateName}
            <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold ${statusPill(submission.status)}`}>
              {submission.status}
            </span>
          </h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => downloadSubmissionPdf(submission.id || id!, submissionPdfFilename(templateName, submission.id || id!))}
              className="flex items-center gap-1.5 px-3 py-2 bg-fieldiq-card border border-fieldiq-border text-white rounded-lg text-sm font-medium hover:bg-fieldiq-border"
            >
              <Download size={16} aria-hidden /> Download PDF
            </button>
            <button
              type="button"
              onClick={() => printSubmissionPdf(submission.id || id!)}
              className="flex items-center gap-1.5 px-3 py-2 bg-fieldiq-card border border-fieldiq-border text-white rounded-lg text-sm font-medium hover:bg-fieldiq-border"
            >
              <Printer size={16} aria-hidden /> Print
            </button>
          </div>
        </div>
      </div>

      <div className="px-6 py-4 space-y-6 max-w-3xl">
        {/* Submission metadata */}
        <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-2">
          <div className="flex items-center gap-2 text-sm text-fieldiq-text">
            <User size={16} className="text-fieldiq-accent" />
            <span>{submitterName || '—'}</span>
          </div>
          {submission.submitted_at && (
            <div className="flex items-center gap-2 text-sm text-fieldiq-text">
              <Calendar size={16} className="text-fieldiq-accent" />
              <span>{format(new Date(submission.submitted_at), 'MMMM d, yyyy h:mm a')}</span>
            </div>
          )}
          {/* The row stores gps_latitude / gps_longitude; there is no
              gps_location object on it, so this block never rendered. */}
          {hasGps && (
            <div className="flex items-center gap-2 text-sm text-fieldiq-text">
              <MapPin size={16} className="text-fieldiq-accent" />
              <span>
                {Number(submission.gps_latitude).toFixed(6)}, {Number(submission.gps_longitude).toFixed(6)}
                {submission.location_name && ` — ${submission.location_name}`}
              </span>
            </div>
          )}
          {(submission.job_name || submission.jobName) && (
            <div className="text-sm text-fieldiq-accent">
              Job: {submission.job_name || submission.jobName}
            </div>
          )}
        </div>

        {/* Rendered fields + answers */}
        <div className="space-y-4">
          {(fields as any[]).map((field: any) => {
            const answer = answers[field.id];

            return (
              <div key={field.id} className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4">
                {field.field_type === 'section_header' && (
                  <h3 className="text-sm font-bold text-white">{field.label}</h3>
                )}

                {field.field_type === 'instructions' && (
                  <p className="text-sm text-fieldiq-muted italic">{field.label}</p>
                )}

                {!['section_header', 'instructions'].includes(field.field_type) && (
                  <>
                    <p className="text-xs text-fieldiq-text mb-1">{field.label}</p>

                    {/* Text/textarea/number/date/time/dropdown/radio */}
                    {['text', 'textarea', 'number', 'date', 'time', 'dropdown', 'radio'].includes(field.field_type) && (
                      <p className="text-sm text-white">{answer || <span className="text-fieldiq-muted">—</span>}</p>
                    )}

                    {/* Yes/No */}
                    {field.field_type === 'yes_no' && (
                      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                        answer === 'Yes' ? 'bg-green-500/20 text-green-400' : answer === 'No' ? 'bg-red-500/20 text-red-400' : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {answer || '—'}
                      </span>
                    )}

                    {/* Checkbox group */}
                    {field.field_type === 'checkbox_group' && (
                      <div className="flex flex-wrap gap-1.5">
                        {Array.isArray(answer) && answer.length > 0 ? answer.map((v: string) => (
                          <span key={v} className="inline-flex items-center rounded-full bg-fieldiq-border px-2 py-0.5 text-xs text-white">
                            {v}
                          </span>
                        )) : <span className="text-fieldiq-muted text-sm">—</span>}
                      </div>
                    )}

                    {/* Photo */}
                    {field.field_type === 'photo' && (
                      <div className="flex flex-wrap gap-2 mt-1">
                        {photos
                          .filter((p: any) => p.field_id === field.id)
                          .map((photo: any, idx: number) => (
                            <button
                              key={idx}
                              onClick={() => setLightboxUrl(photo.url)}
                              className="w-20 h-20 rounded-lg overflow-hidden border border-fieldiq-border hover:border-fieldiq-accent transition-colors"
                            >
                              <img src={photo.url || photo.thumbnail_url} alt="" className="w-full h-full object-cover" />
                            </button>
                          ))}
                        {photos.filter((p: any) => p.field_id === field.id).length === 0 && (
                          <span className="text-fieldiq-muted text-sm">No photos</span>
                        )}
                      </div>
                    )}

                    {/* Signature */}
                    {field.field_type === 'signature' && (
                      <div className="mt-1">
                        {(() => {
                          const sig = signatures.find((s: any) => s.field_id === field.id) || (answer ? { signature_data: answer } : null);
                          if (sig) {
                            return (
                              <div className="bg-white rounded-lg p-2 inline-block">
                                <img src={sig.signature_data || sig.url} alt="Signature" className="max-h-[80px]" />
                              </div>
                            );
                          }
                          return <span className="text-fieldiq-muted text-sm">No signature</span>;
                        })()}
                      </div>
                    )}

                    {/* GPS Location */}
                    {field.field_type === 'gps_location' && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin size={14} className="text-fieldiq-accent" />
                        <span className="text-white">{answer || <span className="text-fieldiq-muted">—</span>}</span>
                      </div>
                    )}

                    {/* Worker select */}
                    {field.field_type === 'worker_select' && (
                      <div className="flex flex-wrap gap-1.5">
                        {/* Names from the API, never the raw user id. */}
                        {Array.isArray(resolved[field.id]) && resolved[field.id].length > 0 ? resolved[field.id].map((p: any) => (
                          <span key={p.id} className="inline-flex items-center rounded-full bg-fieldiq-border px-2 py-0.5 text-xs text-white">
                            {p.name || '—'}
                          </span>
                        )) : <span className="text-fieldiq-muted text-sm">—</span>}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>

        {/* Review section (admin only) */}
        {isAdmin && submission.status !== 'draft' && (
          <div className="bg-fieldiq-card border border-fieldiq-border rounded-xl p-4 space-y-3">
            <h3 className="text-sm font-bold text-white flex items-center gap-2">
              <CheckCircle size={16} className="text-fieldiq-accent" />
              Review
            </h3>
            <div>
              <label className="text-xs text-fieldiq-text mb-1 block">Status</label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setReviewStatus('reviewed')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    reviewStatus === 'reviewed'
                      ? 'bg-green-500 text-white'
                      : 'bg-fieldiq-border text-fieldiq-text hover:text-white'
                  }`}
                >
                  <CheckCircle size={14} />
                  Reviewed
                </button>
                <button
                  type="button"
                  onClick={() => setReviewStatus('flagged')}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    reviewStatus === 'flagged'
                      ? 'bg-red-500 text-white'
                      : 'bg-fieldiq-border text-fieldiq-text hover:text-white'
                  }`}
                >
                  <Flag size={14} />
                  Flagged
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs text-fieldiq-text mb-1 block">Review Notes</label>
              <textarea
                value={reviewNotes}
                onChange={e => setReviewNotes(e.target.value)}
                rows={3}
                placeholder="Add review notes..."
                className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-fieldiq-accent resize-none"
              />
            </div>
            <button
              onClick={() => reviewMutation.mutate()}
              disabled={!reviewStatus || reviewMutation.isPending}
              className="px-4 py-2 bg-fieldiq-accent text-white rounded-lg text-sm font-medium hover:bg-fieldiq-accent/90 disabled:opacity-50"
            >
              {reviewMutation.isPending ? 'Saving...' : 'Save Review'}
            </button>
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-fieldiq-accent"
            onClick={() => setLightboxUrl(null)}
          >
            <X size={24} />
          </button>
          <img src={lightboxUrl} alt="" className="max-w-full max-h-full object-contain rounded-lg" />
        </div>
      )}
    </div>
  );
}
